import React, { FC, useMemo, useCallback } from 'react';
import { InteractiveMap, DropdownComponent, Paper } from 'components';
import {
    WppTypography,
    WppActionButton,
    WppIconReset,
    WppButton,
} from '@wppopen/components-library-react';
import { projectionSlice, updateTabDataStatus } from 'Redux/projectionsSlice';
import { Localization, SelectionAge, TestIds } from 'utils';
import { useSelector, useDispatch } from 'react-redux';
import { Main, SelectionSection, SelectionWrapper } from './SidePanelStyles';
import styles from './Button.module.scss';
import { useLegacyEffect } from 'hooks';
import {
    sidePanelSlice,
    fetchTortoiseData,
    updatePanelSelection,
    updatePanelSubcategorySelection,
    resetSidePanel,
} from 'Redux/sidePanelSlice';

const { ButtonWrapper } = styles;

const DROP_DOWN_WIDTH = '248px';

interface SidePanelProps {
    title: string;
    localization: Localization;
}

export const SidePanel: FC<SidePanelProps> = ({ title, localization }) => {
    const {
        [localization]: {
            ['tabs']: {
                [title]: {
                    selectionVariants: {
                        isRegionShow,
                        isCategoryShow,
                        isSubcategoriesShow,
                        isEthnicShow,
                        isAgeShow,
                        isSexShow,
                        isIncomeShow,
                    },
                    isHareLoading,
                },
            },
        },
    } = useSelector(projectionSlice);

    const {
        [localization]: {
            selectionState: {
                dropDownAllCategories,
                dropDownAllSubcategories,
                dropDownHeritage,
                geoMapSelection,
                age,
                income,
                sex,
            },
            selectionData,
            isTortoiseLoading,
            tortoiseResponse,
            error,
        },
    } = useSelector(sidePanelSlice);
    const dispatch = useDispatch();

    const handleGeoMapState = (dmaData: any, regionData: any, currentSegment: any) => {
        const updatedData = {
            DMA: dmaData,
            REGION: [...regionData],
            Segment: currentSegment,
        };
        dispatch(
            updatePanelSelection({
                property: 'geoMapSelection',
                value: updatedData,
                localization: localization,
            }),
        );
    };

    const handleSelection = (property: string, value: any) => {
        dispatch(
            updatePanelSelection({
                property,
                value,
                localization: localization,
            }),
        );
    };

    const handleReset = () => {
        dispatch(resetSidePanel({ localization: localization }));
    };

    const handleSubcategories = useCallback(
        (property: string, value: string) => {
            dispatch(
                updatePanelSubcategorySelection({
                    property,
                    value,
                    localization: localization,
                }),
            );
        },
        [dispatch, localization],
    );

    const handleRequest = async () => {
        await dispatch(
            fetchTortoiseData({
                tab: title,
                sidePanelPayload: {
                    animal: 'tortoise',
                    COUNTRY: localization,
                    ...(geoMapSelection?.Segment && {
                        GEO_UNIT:
                            localization === Localization.US ? geoMapSelection?.Segment : 'Country',
                    }),
                    ...(geoMapSelection[geoMapSelection?.Segment] && {
                        GEO_NAME:
                            localization === Localization.US
                                ? geoMapSelection[geoMapSelection?.Segment]
                                : 'United Kingdom',
                    }),
                    ...(isEthnicShow && { ETH_GRP: dropDownHeritage }),
                    ...(isSexShow && { SEX: sex }),
                    ...(isAgeShow && {
                        AGE_GRP: [
                            ...(title === 'Population' && localization === Localization.US
                                ? [SelectionAge.GROUP_LESS_18]
                                : []),
                            ...age,
                        ],
                    }),
                    ...(isIncomeShow && { INCOME_BRACKET: income }),
                    ...(isCategoryShow && { CATEGORY: dropDownAllCategories }),
                    ...(isSubcategoriesShow && {
                        SUBCATEGORY: dropDownAllSubcategories?.[dropDownAllCategories],
                    }),
                },
                localization,
                isTortoiseUpdate: true,
            }),
        );
        await dispatch(
            updateTabDataStatus({
                localization: localization,
            }),
        );
    };

    const SubcategoriesSelection = useMemo(() => {
        return (
            <>
                {dropDownAllSubcategories?.[dropDownAllCategories] && (
                    <DropdownComponent
                        placeholder="select"
                        value={dropDownAllSubcategories?.[dropDownAllCategories]}
                        handleChange={handleSubcategories}
                        keyProp={dropDownAllCategories}
                        selectionData={selectionData.dropDownAllSubcategories}
                        containerWidth={DROP_DOWN_WIDTH}
                        dropdownWidth={DROP_DOWN_WIDTH}
                        allSelectedText="All subcategories"
                        inputDisabled={isTortoiseLoading || isHareLoading}
                        wppSize="m"
                        dataTestId="PROJECTION.SIDEPANEL.SECTION.SUBCATEGORY"
                    />
                )}
            </>
        );
    }, [
        dropDownAllCategories,
        dropDownAllSubcategories,
        selectionData.dropDownAllSubcategories,
        handleSubcategories,
        isTortoiseLoading,
        isHareLoading,
    ]);

    useLegacyEffect(() => {
        !error &&
            !isTortoiseLoading &&
            !tortoiseResponse &&
            dispatch(
                fetchTortoiseData({
                    tab: title,
                    sidePanelPayload: {
                        animal: 'tortoise',
                        COUNTRY: localization,
                        ...(geoMapSelection?.Segment && {
                            GEO_UNIT:
                                localization === Localization.US
                                    ? geoMapSelection?.Segment
                                    : 'Country',
                        }),
                        ...(geoMapSelection[geoMapSelection?.Segment] && {
                            GEO_NAME:
                                localization === Localization.US
                                    ? geoMapSelection[geoMapSelection?.Segment]
                                    : 'United Kingdom',
                        }),
                        ...(isEthnicShow && { ETH_GRP: dropDownHeritage }),
                        ...(isSexShow && { SEX: sex }),
                        ...(isAgeShow && {
                            AGE_GRP: [
                                ...(title === 'Population' && localization === Localization.US
                                    ? [SelectionAge.GROUP_LESS_18]
                                    : []),
                                ...age,
                            ],
                        }),
                        ...(isIncomeShow && { INCOME_BRACKET: income }),
                        ...(isCategoryShow && { CATEGORY: dropDownAllCategories }),
                        ...(isSubcategoriesShow && {
                            SUBCATEGORY: dropDownAllSubcategories?.[dropDownAllCategories],
                        }),
                    },
                    localization,
                }),
            );
    }, [
        dispatch,
        title,
        tortoiseResponse,
        isCategoryShow,
        isSubcategoriesShow,
        isIncomeShow,
        isAgeShow,
        isSexShow,
        isEthnicShow,
        geoMapSelection,
        isTortoiseLoading,
        error,
        age,
        dropDownAllCategories,
        dropDownAllSubcategories,
        dropDownHeritage,
        income,
        sex,
        localization,
    ]);

    return (
        <Paper
            width="296px"
            height="fit-content"
            testid={TestIds.projection.sidePanel.mainContainer}
        >
            <Main>
                <SelectionSection>
                    <WppTypography type="l-strong" data-testid={TestIds.projection.sidePanel.title}>
                        Define Segments
                    </WppTypography>
                    <WppActionButton
                        onClick={() => handleReset()}
                        data-testid={TestIds.projection.sidePanel.resetButton}
                    >
                        <WppIconReset slot="icon-start" />
                        Reset
                    </WppActionButton>
                </SelectionSection>
                <SelectionSection isShowing={isRegionShow}>
                    <SelectionWrapper>
                        <WppTypography
                            type="s-strong"
                            data-testid={TestIds.projection.sidePanel.regionSectionTitle}
                        >
                            Region
                        </WppTypography>
                        <InteractiveMap
                            disabled={isTortoiseLoading || isHareLoading}
                            handleData={handleGeoMapState}
                            currentSelection={geoMapSelection}
                            withApplyButton={false}
                            wppSize="m"
                            dataTestId={TestIds.projection.sidePanel.regionSection}
                        />
                    </SelectionWrapper>
                </SelectionSection>
                <SelectionSection isShowing={isCategoryShow}>
                    <SelectionWrapper>
                        <DropdownComponent
                            placeholder="Select category"
                            value={dropDownAllCategories}
                            handleChange={handleSelection}
                            keyProp="dropDownAllCategories"
                            selectionData={selectionData}
                            isSingleSelection={true}
                            containerWidth={DROP_DOWN_WIDTH}
                            dropdownWidth={DROP_DOWN_WIDTH}
                            label="Category"
                            inputDisabled={isTortoiseLoading || isHareLoading}
                            wppSize="m"
                            dataTestId={TestIds.projection.sidePanel.categorySection}
                        />
                    </SelectionWrapper>
                </SelectionSection>
                <SelectionSection isShowing={isSubcategoriesShow}>
                    <SelectionWrapper>{SubcategoriesSelection}</SelectionWrapper>
                </SelectionSection>
                <SelectionSection isShowing={isEthnicShow}>
                    <SelectionWrapper>
                        <DropdownComponent
                            placeholder="Select ethnic group"
                            value={dropDownHeritage}
                            handleChange={handleSelection}
                            keyProp="dropDownHeritage"
                            selectionData={selectionData}
                            containerWidth={DROP_DOWN_WIDTH}
                            dropdownWidth={DROP_DOWN_WIDTH}
                            label="Ethnic groups"
                            allSelectedText="All ethnicities"
                            inputDisabled={isTortoiseLoading || isHareLoading}
                            wppSize="m"
                            dataTestId={TestIds.projection.sidePanel.ethnicSection}
                        />
                    </SelectionWrapper>
                </SelectionSection>
                <SelectionSection isShowing={isAgeShow}>
                    <SelectionWrapper>
                        <DropdownComponent
                            placeholder="Select age"
                            value={age}
                            handleChange={handleSelection}
                            keyProp="age"
                            selectionData={selectionData}
                            containerWidth={DROP_DOWN_WIDTH}
                            dropdownWidth={DROP_DOWN_WIDTH}
                            label="Age"
                            allSelectedText="All ages"
                            inputDisabled={isTortoiseLoading || isHareLoading}
                            wppSize="m"
                            dataTestId={TestIds.projection.sidePanel.ageSection}
                        />
                    </SelectionWrapper>
                </SelectionSection>
                <SelectionSection isShowing={isSexShow}>
                    <SelectionWrapper>
                        <DropdownComponent
                            placeholder="Select sex"
                            value={sex}
                            handleChange={handleSelection}
                            keyProp="sex"
                            selectionData={selectionData}
                            containerWidth={DROP_DOWN_WIDTH}
                            dropdownWidth={DROP_DOWN_WIDTH}
                            label="Sex"
                            allSelectedText="Both"
                            inputDisabled={isTortoiseLoading || isHareLoading}
                            wppSize="m"
                            dataTestId={TestIds.projection.sidePanel.sexSection}
                        />
                    </SelectionWrapper>
                </SelectionSection>
                <SelectionSection isShowing={isIncomeShow}>
                    <SelectionWrapper>
                        <DropdownComponent
                            placeholder="Select income"
                            value={income}
                            handleChange={handleSelection}
                            keyProp="income"
                            selectionData={selectionData}
                            containerWidth={DROP_DOWN_WIDTH}
                            dropdownWidth={DROP_DOWN_WIDTH}
                            label="Disposable income"
                            allSelectedText="All incomes"
                            inputDisabled={isTortoiseLoading || isHareLoading}
                            wppSize="m"
                            dataTestId={TestIds.projection.sidePanel.incomeSection}
                        />
                    </SelectionWrapper>
                </SelectionSection>
                <WppButton
                    size="m"
                    className={ButtonWrapper}
                    disabled={isTortoiseLoading || isHareLoading}
                    onClick={handleRequest}
                    data-testid={TestIds.projection.sidePanel.runButton}
                >
                    Run
                </WppButton>
            </Main>
        </Paper>
    );
};
